// Clipboard + download utilities that work inside restricted iframes
// (Emergent preview embeds the app in a cross-origin iframe that often
// blocks navigator.clipboard and the anchor-download flow).

const IS_DEV = process.env.NODE_ENV === "development";
const debug = (...args) => { if (IS_DEV) console.debug(...args); };

export async function copyText(text) {
  // Try the modern API first
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return { ok: true, method: "clipboard" };
    }
  } catch (err) {
    debug("clipboard.writeText failed, falling back:", err?.message || err);
  }

  // Legacy execCommand path — works inside most iframes
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) return { ok: true, method: "execCommand" };
  } catch (err) {
    debug("execCommand copy fallback failed:", err?.message || err);
  }

  return { ok: false };
}

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  try {
    const w = window.open(url, "_blank", "noopener");
    if (w) {
      try { w.document.title = filename; } catch (err) { debug("cross-origin title set failed:", err?.message || err); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return { ok: true, method: "newtab", url };
    }
  } catch (err) {
    debug("window.open blob failed, trying anchor:", err?.message || err);
  }

  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return { ok: true, method: "anchor", url };
  } catch (err) {
    debug("anchor download failed:", err?.message || err);
    URL.revokeObjectURL(url);
    return { ok: false };
  }
}

export function saveRemoteFile(fileUrl) {
  // Simplest reliable path: open the file URL in a new tab. In a browser
  // that's not iframed → the download attribute wins. In an iframe → the
  // new tab lives outside the sandbox so the user can Save As.
  try {
    const w = window.open(fileUrl, "_blank", "noopener");
    if (w) return { ok: true, method: "newtab" };
    throw new Error("popup blocked");
  } catch (err) {
    debug("popup blocked, falling back to same-tab navigation:", err?.message || err);
    // Fallback: navigate the current window (destroys app state but works)
    try {
      window.location.href = fileUrl;
      return { ok: true, method: "navigate" };
    } catch (err2) {
      debug("same-tab navigation also failed:", err2?.message || err2);
      return { ok: false };
    }
  }
}
